<template>
  <main>
    <section class="section banner page-title-banner">
      <div class="shell">
        <header class="page-head compact">
          <h1>What a maintenance plan costs</h1>
        </header>
      </div>
    </section>

    <!-- The scope guard sits above the number rather than under it. Someone who
         reads one thing on this page reads the headline figure, so the thing
         that thing does NOT cover has to arrive first. -->
    <section class="section scope-wrap">
      <div class="shell">
        <div class="card panel scope">
          <p class="eyebrow">Read this first</p>
          <p>
            This estimates the <strong>managed maintenance plan</strong> only — the monthly
            subscription and the travel to reach your machines.
            <strong>It does not price repair work.</strong> Breakdown callouts, diagnostics, and
            shop labour run on a separate, higher rate schedule and are quoted per job.
          </p>
          <router-link class="btn btn-ghost" to="/services/on-site-repairs">
            Repair service and rates
          </router-link>
        </div>

        <p class="calc-lead">
          Move the two sliders to see an estimate of the monthly total. The travel expense on
          larger ranches is the piece of this estimate that will likely be the most flexible.
          When we come by to do the initial onboarding, we will use the information from that
          visit to finalize the true travel cost and what the overall monthly numbers will be.
        </p>
      </div>
    </section>

    <section class="section calc-section">
      <div class="shell estimate-grid">
        <div class="card panel calc">
          <div class="controls">
            <div class="field-slider">
              <div class="field-head">
                <label for="miles">Miles to your property</label>
                <span class="field-value">{{ miles }} mi one way</span>
              </div>
              <input id="miles" v-model.number="miles" type="range" min="5" max="120" step="1" />
            </div>

            <div class="field-slider">
              <div class="field-head">
                <label for="machines">Machines on the program</label>
                <span class="field-value">{{ machines }} {{ machines === 1 ? 'machine' : 'machines' }}</span>
              </div>
              <input id="machines" v-model.number="machines" type="range" min="1" max="45" step="1" />
              <p class="field-hint">
                One travel charge per trip, shared by every machine we service on it. Past
                {{ DISCOUNT_FREE_MACHINES }} machines the charge drops
                {{ Math.round(DISCOUNT_PER_MACHINE * 100) }}% for each machine after that, to a
                maximum of {{ Math.round(DISCOUNT_CAP * 100) }}% — the travel charge covers fuel
                and the drive, so it is not discounted past the point where it stops doing that.
              </p>
            </div>
          </div>

          <div class="readout" aria-live="polite">
            <div class="cell">
              <span class="cell-label">Subscription</span>
              <span class="cell-value">{{ money(result.subscription) }}</span>
              <span class="cell-note">{{ money(BASELINE) }} × {{ machines }}</span>
            </div>
            <div class="cell">
              <span class="cell-label">Travel per visit</span>
              <span class="cell-value">{{ money(result.travel) }}</span>
              <span class="cell-note">{{ splitLabel }}</span>
            </div>
            <div class="cell hero">
              <span class="cell-label">Per month</span>
              <span class="cell-value">{{ money(result.monthlyTotal) }}</span>
              <span class="cell-note">{{ money(result.monthlyTotal / machines) }} per machine</span>
            </div>
          </div>

          <p class="calc-foot">
            Travel works out to {{ money(result.travelPerMachine) }} per machine.
            <template v-if="machines === 1">
              A second machine on the same trip halves that.
            </template>
            <template v-else>
              On its own, one machine would carry {{ money(soloTravel) }}.
            </template>
            <template v-if="result.visits > 1">
              This assumes {{ result.visits }} trips — if your machines turn out to fit in
              fewer, so does the travel.
            </template>
          </p>

          <!-- Add-ons are not priced by the estimator — greasing needs a real
               zerk count per machine and cleaning depends on the machine — so
               these mark interest for the callback rather than moving the
               figure above them. -->
          <fieldset class="interests">
            <legend>Any other maintenance services of interest</legend>
            <div class="interest-row">
              <label v-for="option in interestOptions" :key="option.key" class="interest">
                <input v-model="interests[option.key]" type="checkbox" />
                <span>{{ option.label }}</span>
              </label>
            </div>
          </fieldset>

          <!-- The ask sits on the figure rather than at the foot of the page.
               Name and number only: this is a "call me about this" button, and
               every extra field is a reason not to press it. The estimate goes
               with it, so whoever calls already has the numbers. -->
          <div v-if="submitted" class="sent" role="status">
            <p><strong>Got it, {{ submittedName }}.</strong> Your numbers came through with it, so
              whoever calls will have them in front of them.</p>
            <p class="fine">Sooner is fine too — <a :href="phoneHref">{{ brand.phone }}</a>.</p>
          </div>

          <form v-else class="ask" novalidate @submit.prevent="handleSubmit">
            <p class="ask-lead">Want us to go over it with you?</p>
            <div class="ask-row">
              <div class="field">
                <label for="name">Name</label>
                <input id="name" v-model="form.name" type="text" autocomplete="name"
                       :aria-invalid="!!errors.name" />
              </div>
              <div class="field">
                <label for="phone">Number to call</label>
                <input id="phone" v-model="form.phone" type="tel" autocomplete="tel"
                       :aria-invalid="!!errors.phone" />
              </div>
              <button class="btn btn-primary" type="submit" :disabled="isSubmitting">
                {{ isSubmitting ? 'Sending…' : 'Send' }}
              </button>
            </div>

            <!-- Honeypot. Real people never see it, so anything in it is a bot. -->
            <div class="honeypot" aria-hidden="true">
              <label for="company">Company</label>
              <input id="company" v-model="form.company" type="text" tabindex="-1"
                     autocomplete="off" />
            </div>

            <p v-if="errors.name || errors.phone" class="field-error">
              {{ errors.name || errors.phone }}
            </p>
            <p v-if="submitError" class="field-error" role="alert">{{ submitError }}</p>
          </form>
        </div>

        <aside class="card panel covers">
          <h2>What the monthly covers</h2>
          <ul>
            <li>A QR tag fixed to the machine, tracking all service done</li>
            <li>The scheduled visit, and eyes on every machine</li>
            <li>Fluids topped off, and what it took written down</li>
            <li>Hour meter read and logged</li>
            <li>A look over for visible wear and leaks</li>
            <li>The whole record kept in the Machine Portal, open to you any time</li>
          </ul>

          <h2>Billed separately</h2>
          <ul class="separate">
            <li>Oil changes, filters, and parts</li>
            <li>Repairs of any kind, at repair rates</li>
            <li>Greasing, per fitting, if you add it</li>
            <li>Washing, if you add it</li>
          </ul>
          <p class="fine">
            Add-ons are additions to the plan, not the reason for it. Greasing runs on a sliding
            per-fitting scale starting at {{ money(ZERK_TIERS[0][1]) }} and falling to
            {{ money(ZERK_TIERS[ZERK_TIERS.length - 1][1]) }} on high-count machines.
          </p>
        </aside>
      </div>
    </section>

  </main>
</template>

<script>
import { brand } from '@/data/content';
import {
  BASELINE,
  MILE_RATE,
  TRIP_FACTOR,
  DISCOUNT_PER_MACHINE,
  DISCOUNT_FREE_MACHINES,
  DISCOUNT_CAP,
  MAX_MACHINES_PER_VISIT,
  ZERK_TIERS,
  estimate,
  money,
} from '@/data/planPricing';

const ENDPOINT = '/api/service-request';

/* Must appear verbatim in SERVICE_TYPES in functions/index.js — the server
   rejects anything not on its allowlist. It has its own type rather than
   borrowing the general maintenance one so the function can route it: while
   the pricing is being settled these land with Daniel alone, and they read
   differently in an inbox anyway, arriving with a priced estimate attached. */
const SERVICE_TYPE = 'Maintenance plan estimate';

function emptyForm() {
  return { name: '', email: '', phone: '', location: '', details: '', company: '' };
}

/* Ticking one of these does not change the estimate, and must not look as
   though it should: greasing is priced per fitting off a real zerk count and
   cleaning depends on the machine, so neither can be quoted from two sliders.
   They ride along with the callback so the conversation starts in the right
   place. */
const INTEREST_OPTIONS = [
  { key: 'greasing', label: 'Greasing', report: 'greasing' },
  { key: 'cleaning', label: 'Machine cleaning', report: 'machine cleaning' },
  { key: 'oilChange', label: 'Annual oil change', report: 'annual oil change' },
];

function emptyInterests() {
  return Object.fromEntries(INTEREST_OPTIONS.map((option) => [option.key, false]));
}

export default {
  name: 'MaintenanceEstimateView',
  data() {
    return {
      brand,
      BASELINE,
      MILE_RATE,
      TRIP_FACTOR,
      DISCOUNT_PER_MACHINE,
      DISCOUNT_FREE_MACHINES,
      DISCOUNT_CAP,
      MAX_MACHINES_PER_VISIT,
      ZERK_TIERS,
      money,
      miles: 40,
      machines: 5,
      interestOptions: INTEREST_OPTIONS,
      interests: emptyInterests(),
      form: emptyForm(),
      errors: {},
      isSubmitting: false,
      submitError: '',
      submitted: false,
      submittedName: '',
    };
  },
  computed: {
    result() {
      return estimate({ oneWayMiles: this.miles, machines: this.machines });
    },
    soloTravel() {
      return estimate({ oneWayMiles: this.miles, machines: 1 }).travel;
    },
    splitLabel() {
      const { visits, groups, discountPercent } = this.result;
      const shape = visits === 1 ? 'one trip' : `${visits} trips, ${groups.join(' + ')}`;
      return discountPercent > 0 ? `${shape} · ${discountPercent}% off` : shape;
    },
    phoneHref() {
      return `tel:${this.brand.phone.replace(/\D/g, '')}`;
    },
  },
  methods: {
    /* Name and number only. The server excuses email and location for this one
       service type, so nothing else is asked for or sent. */
    validate() {
      const errors = {};
      if (this.form.name.trim().length < 2) errors.name = 'Please enter your name.';
      if (this.form.phone.replace(/\D/g, '').length < 10) {
        errors.phone = 'Please enter a number we can call you back on.';
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    /* The estimate is written into the details field rather than sent as extra
       keys, because the server only relays the six fields it validates —
       anything else would be dropped silently and the team would get a callback
       request with no numbers attached. */
    composeDetails() {
      const r = this.result;
      const lines = [
        'Maintenance plan estimate requested from the website.',
        '',
        `Machines: ${this.machines}`,
        `Distance: ${this.miles} miles one way (${r.billableMiles} billable)`,
        `Visits: ${r.visits}${r.visits > 1 ? ` (${r.groups.join(' + ')})` : ''}`,
        `Subscription: ${money(r.subscription)}`,
        `Travel: ${money(r.travel)}${r.discountPercent > 0 ? ` after ${r.discountPercent}% multi-machine discount` : ''}`,
        `Estimated monthly: ${money(r.monthlyTotal)}`,
      ];

      const wants = INTEREST_OPTIONS.filter((o) => this.interests[o.key]).map((o) => o.report);
      if (wants.length) lines.push('', `Also asked about: ${wants.join(', ')} (not in the figures above)`);

      const note = this.form.details.trim();
      if (note) lines.push('', `Their note: ${note}`);
      return lines.join('\n');
    },
    async handleSubmit() {
      this.submitError = '';
      if (!this.validate()) return;

      this.isSubmitting = true;
      try {
        const response = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: this.form.name,
            email: this.form.email,
            phone: this.form.phone,
            location: this.form.location,
            serviceType: SERVICE_TYPE,
            details: this.composeDetails(),
            company: this.form.company,
          }),
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'Something went wrong sending your request.');
        }

        this.submittedName = this.form.name.trim();
        this.submitted = true;
        this.form = emptyForm();
        this.interests = emptyInterests();
        this.errors = {};
      } catch (error) {
        this.submitError = `${error.message} You can also call us at ${brand.phone}.`;
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.banner {
  background: var(--black);
  border-bottom: 1px solid var(--orange);
}

/* The logo plate is absolutely positioned and hangs 137px below the nav across
   the left of the page, so a section starting at the normal 72px runs beneath
   it. This is the floor, not a preference — it is the plate reach plus a little,
   trimmed as far as it will go so the calculator climbs above the fold. */
.page-title-banner {
  padding-top: clamp(9.25rem, 12vw, 9.25rem);
  padding-bottom: 0.85rem;
}

/* 18ch broke this into three lines and cost 64px of fold. It wants two. */
.page-title-banner h1 {
  font-size: clamp(2.2rem, 5.4vw, 4rem);
  font-weight: 200;
  line-height: 1.02;
  letter-spacing: 0.018em;
  text-transform: uppercase;
  max-width: 24ch;
}

/* Everything from the heading down to the calculator is squeezed so the readout
   breaks the fold on a normal display. The calculator is the reason anyone came
   to this page; if it sits entirely below the scroll it may as well not be on
   it. */
.scope-wrap {
  padding-top: 0.75rem;
  padding-bottom: 0;
}

.scope {
  display: grid;
  gap: 0.7rem;
  justify-items: start;
  padding: 1rem 1.15rem;
  border-color: var(--orange);
}

/* 88ch, not narrower and not wider. At 62ch this ran to five lines and pushed
   the calculator 55px further down the page; 88ch settles it at three, and
   anything wider than that stays at three while making the measure longer to
   read for nothing. */
.calc-lead {
  margin-top: 0.85rem;
  max-width: 88ch;
  opacity: 0.74;
}

.calc-section {
  padding-top: 0.85rem;
}

.scope strong {
  color: var(--orange);
  font-weight: 600;
}

.estimate-grid {
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  gap: clamp(1.2rem, 3vw, 2rem);
  align-items: start;
}

.calc {
  display: grid;
  gap: 1.6rem;
}

.controls {
  display: grid;
  gap: 1.5rem;
}

.field-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.field-head label {
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-size: 0.8rem;
}

.field-value {
  font-variant-numeric: tabular-nums;
  color: var(--orange);
  font-weight: 600;
}

.field-hint {
  margin-top: 0.5rem;
  font-size: 0.88rem;
  color: var(--white);
  opacity: 0.66;
}

input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 1.5rem;
  background: transparent;
  cursor: pointer;
}

input[type='range']::-webkit-slider-runnable-track {
  height: 3px;
  background: rgba(255, 255, 255, 0.28);
}

input[type='range']::-moz-range-track {
  height: 3px;
  background: rgba(255, 255, 255, 0.28);
}

input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 1.1rem;
  height: 1.1rem;
  margin-top: -0.5rem;
  border-radius: 50%;
  background: var(--orange);
  border: 2px solid var(--black);
}

input[type='range']::-moz-range-thumb {
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  background: var(--orange);
  border: 2px solid var(--black);
}

.readout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 1px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.cell {
  background: var(--black);
  padding: 0.9rem 1rem;
  display: grid;
  gap: 0.25rem;
}

.cell-label {
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.68rem;
  font-weight: 700;
  opacity: 0.62;
}

.cell-value {
  font-size: clamp(1.3rem, 3.4vw, 1.7rem);
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}

.cell-note {
  font-size: 0.8rem;
  opacity: 0.6;
  font-variant-numeric: tabular-nums;
}

.cell.hero {
  background: var(--orange);
  color: var(--black);
}

.cell.hero .cell-label,
.cell.hero .cell-note {
  opacity: 0.78;
}

.calc-foot {
  font-size: 0.92rem;
  opacity: 0.72;
}

.interests {
  margin: 0;
  padding: 1.2rem 0 0;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
}

/* Short labels, so they sit on one line and wrap only when they must. */
.interest-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem 1.4rem;
}

.interests legend {
  padding: 0;
  margin-bottom: 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.62;
}

.interest {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  font-size: 0.95rem;
}

.interest input {
  width: 1.05rem;
  height: 1.05rem;
  flex: 0 0 auto;
  accent-color: var(--orange);
  cursor: pointer;
}

.interest input:focus-visible {
  outline: 2px solid var(--orange);
  outline-offset: 2px;
}

/* Separated by a rule rather than another card — it belongs to the figure
   above it, not beside it. */
.ask,
.sent {
  display: grid;
  gap: 0.7rem;
  padding-top: 1.2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
}

.ask-lead {
  font-weight: 600;
  letter-spacing: 0.01em;
}

.ask-row {
  display: flex;
  flex-wrap: wrap;
  align-items: end;
  gap: 0.7rem;
}

.ask-row .field {
  flex: 1 1 9rem;
  display: grid;
  gap: 0.3rem;
  min-width: 0;
}

.ask-row label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.62;
}

.ask-row input {
  width: 100%;
  padding: 0.6rem 0.7rem;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: var(--radius-sm);
  color: var(--white);
  font: inherit;
  font-size: 0.95rem;
}

.ask-row input:focus-visible {
  outline: 2px solid var(--orange);
  outline-offset: 1px;
}

.ask-row input[aria-invalid='true'] {
  border-color: var(--orange);
}

.ask-row .btn {
  flex: 0 0 auto;
  padding-block: 0.62rem;
}

.field-error {
  color: var(--orange);
  font-size: 0.86rem;
}

.sent a {
  color: var(--orange);
  font-weight: 600;
}

.covers h2 {
  font-size: 1.05rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.6rem;
}

.covers h2 + ul {
  margin-bottom: 1.5rem;
}

.covers ul {
  margin: 0;
  padding-left: 1.05rem;
  display: grid;
  gap: 0.4rem;
}

.covers li {
  line-height: 1.5;
}

.covers .separate {
  color: var(--white);
  opacity: 0.72;
}

.fine {
  margin-top: 1rem;
  font-size: 0.86rem;
  opacity: 0.62;
}












.honeypot {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
}

@media (max-width: 900px) {
  .estimate-grid {
    grid-template-columns: 1fr;
  }
}
</style>
